import type { ComponentType, ReactNode } from 'react';

export type PillTone = 'gain' | 'loss' | 'warning' | 'rose' | 'accent' | 'neutral';

interface Props {
  tone?: PillTone;
  /** Override the auto-mapped background. */
  bg?: string;
  /** Override the auto-mapped foreground. */
  fg?: string;
  icon?: ComponentType<{ className?: string }>;
  title?: string;
  className?: string;
  children: ReactNode;
}

const toneStyles: Record<PillTone, { bg: string; fg: string }> = {
  gain:    { bg: 'var(--gain-soft)',    fg: 'var(--chip-text)' },
  loss:    { bg: 'var(--loss-soft)',    fg: 'var(--chip-text)' },
  // Amber — used for breakeven / pending. Chip text stays the high-contrast neutral.
  warning: { bg: 'var(--warning-soft)', fg: 'var(--chip-text)' },
  // Pink-rose — used for OPEN/active states. Foreground tracks the loss colour in light
  // (per Figma) and falls back to chip-text in dark via the var.
  rose:    { bg: 'var(--rose-soft)',    fg: 'var(--loss)' },
  accent:  { bg: 'var(--accent-soft)',  fg: 'var(--chip-text)' },
  neutral: { bg: 'var(--canvas-inset)', fg: 'var(--text-muted)' },
};

export function Pill({ tone, bg, fg, icon: Icon, title, className = '', children }: Props) {
  const auto = tone ? toneStyles[tone] : undefined;
  const style: React.CSSProperties = {
    background: bg ?? auto?.bg,
    color:      fg ?? auto?.fg,
  };
  return (
    <span className={`pill ${className}`} style={style} title={title}>
      {Icon && <Icon className="h-3 w-3" />}
      {children}
    </span>
  );
}
